import { useCallback, useEffect, useState } from 'react';

/**
 * Document Picture-in-Picture (Chrome/Edge): opens a real OS-level window that
 * can hold arbitrary DOM, so — unlike native video PiP — we can render our OWN
 * player controls (quality, captions, volume, go-live) in a window that floats
 * over other tabs and apps. We portal the player into `pipWindow` and copy the
 * page's stylesheets + theme so it looks identical.
 */
export function useDocumentPip() {
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const supported =
    typeof window !== 'undefined' && 'documentPictureInPicture' in window;

  const open = useCallback(
    async (opts?: { width?: number; height?: number }) => {
      if (!supported) return null;
      try {
        const w: Window = await (window as any).documentPictureInPicture.requestWindow({
          width: opts?.width ?? 480,
          height: opts?.height ?? 280,
        });

        // Replicate styles into the PiP document. The PiP window is about:blank,
        // so a cloned <link> keeps its RELATIVE href and fails to load — recreate
        // links with their ABSOLUTE URL; inline <style> can be cloned directly.
        document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
          if (node.tagName === 'LINK') {
            const link = w.document.createElement('link');
            link.rel = 'stylesheet';
            link.href = (node as HTMLLinkElement).href; // resolved absolute URL
            w.document.head.appendChild(link);
          } else {
            w.document.head.appendChild(node.cloneNode(true));
          }
        });
        // Also carry over any constructed/adopted stylesheets (rebuilt in the
        // PiP realm; same-origin rules are readable).
        try {
          const adopted: CSSStyleSheet[] = [];
          for (const sheet of (document as any).adoptedStyleSheets || []) {
            try {
              const css = Array.from(sheet.cssRules).map((r: any) => r.cssText).join('\n');
              const clone = new (w as any).CSSStyleSheet();
              clone.replaceSync(css);
              adopted.push(clone);
            } catch {
              /* cross-origin sheet — skip */
            }
          }
          if (adopted.length) (w.document as any).adoptedStyleSheets = adopted;
        } catch {
          /* adoptedStyleSheets unsupported — links above are enough */
        }
        // Carry over the theme: dark class + accent CSS variables on :root.
        w.document.documentElement.className = document.documentElement.className;
        const rootStyle = document.documentElement.getAttribute('style');
        if (rootStyle) w.document.documentElement.setAttribute('style', rootStyle);
        w.document.body.style.margin = '0';
        w.document.body.style.background = '#000';

        w.addEventListener('pagehide', () => setPipWindow(null), { once: true });
        setPipWindow(w);
        return w;
      } catch (e) {
        console.error('Document PiP failed:', e);
        return null;
      }
    },
    [supported]
  );

  const close = useCallback(() => {
    setPipWindow((w) => {
      if (w) {
        try {
          w.close();
        } catch {
          /* noop */
        }
      }
      return null;
    });
  }, []);

  // Close the PiP window if the component unmounts.
  useEffect(
    () => () => {
      if (pipWindow) {
        try {
          pipWindow.close();
        } catch {
          /* noop */
        }
      }
    },
    [pipWindow]
  );

  return { supported, pipWindow, open, close };
}
